import { FC, useMemo } from "react";
import cn from "classnames";
import Icon from "~/components/shared/Icon";
import { parseSimpleEmoji } from "~/lib/parseEmoji";

type EmojiAvatarProps = {
  emoji?: string | null;
  color?: string | null;
  size?: Size;
  fallbackIcon?: string;
  fallbackColor?: string;
  className?: string;
  testId?: string;
};

const SIZE_MAP = {
  sm: { containerSize: "size-6", containerClasses: "text-xs rounded-sm", emoji: "size-4" },
  base: { containerSize: "size-10", containerClasses: "text-base rounded-sm", emoji: "size-5" },
  lg: { containerSize: "size-12", containerClasses: "text-xl rounded-md", emoji: "size-7" },
} as const;

type StringSize = keyof typeof SIZE_MAP;
type Size = StringSize | number;

function getClosestPreset(size: number): StringSize {
  if (size <= 32) {
    return "sm";
  }
  if (size <= 48) {
    return "base";
  }
  return "lg";
}

function useAvatarSizing(size: Size) {
  return useMemo(() => {
    if (typeof size === "number") {
      const preset = SIZE_MAP[getClosestPreset(size)];
      return {
        containerClass: preset.containerClasses,
        sizeClass: undefined,
        containerStyle: { width: `${size}px`, height: `${size}px` },
        emojiClass: preset.emoji,
      };
    }
    const preset = SIZE_MAP[size as StringSize];
    return {
      containerClass: preset.containerClasses,
      sizeClass: preset.containerSize,
      containerStyle: undefined,
      emojiClass: preset.emoji,
    };
  }, [size]);
}

function EmojiContent({ emoji, size }: { emoji: string; size: Size }) {
  const { emojiClass } = useAvatarSizing(size);
  const parsed = useMemo(() => parseSimpleEmoji(emoji), [emoji]);

  if (!parsed) {
    if (/^:.*:$/.test(emoji)) {
      return (
        <span
          title={emoji}
          className={cn("flex items-center justify-center leading-none", emojiClass)}
        >
          {emoji}
        </span>
      );
    }
    return null;
  }

  if (parsed.type === "unicode") {
    return (
      <span
        title={emoji}
        className={cn("ui-emoji flex items-center justify-center leading-none", emojiClass)}
      >
        {parsed.content}
      </span>
    );
  }

  if (parsed.type === "image") {
    return (
      <img
        title={parsed.content}
        src={parsed.url}
        alt={`:${parsed.content}:`}
        className={cn("ui-emoji object-contain", emojiClass)}
        draggable={false}
      />
    );
  }

  return null;
}

function FallbackIcon({ fallbackIcon }: { fallbackIcon: string }) {
  return <Icon icon={fallbackIcon} className="shrink-0" data-testid={fallbackIcon + "-icon"} />;
}

const EmojiAvatar: FC<EmojiAvatarProps> = ({
  emoji,
  color,
  size = "base",
  fallbackIcon = "custom/outline/pipeline",
  fallbackColor = "#F5F5F5",
  className,
  testId = "emoji-avatar-base",
}) => {
  const { containerClass, sizeClass, containerStyle } = useAvatarSizing(size);

  const style = useMemo(
    () => ({
      backgroundColor: color ?? fallbackColor,
      ...containerStyle,
    }),
    [color, fallbackColor, containerStyle],
  );

  const parsedEmoji = useMemo(() => emoji && parseSimpleEmoji(emoji), [emoji]);

  return (
    <div
      className={cn("flex items-center justify-center", sizeClass, containerClass, className)}
      style={style}
      data-testid={testId}
      title={emoji as string}
    >
      {parsedEmoji ? (
        <EmojiContent emoji={emoji as string} size={size} />
      ) : (
        <FallbackIcon fallbackIcon={fallbackIcon} />
      )}
    </div>
  );
};

export default EmojiAvatar;
